import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import type { Rng } from '../rand.ts';
import { M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Wyvern de ceniza (docs/CRIATURAS.md §2, era II): a lesser cousin of the dragon of the Burn, made of what the
 * fire left. A lean two-legged wyvern whose wings are its arms: soot-grey hide cracked like cooled ash with the
 * Burn's heat glowing red in the seams, tattered membranes holed and torn along the trailing edge with smoke
 * streaming off them, a long whip of a tail ending in a barb, and a narrow horned head over a throat sac that
 * glows through its ribs of hide and swells before it spits. It hovers over a tower and spits fire down on it.
 *
 * Lighter and more ragged than the dragon: no glass, no crown, no violet; ash, charcoal and ember only. Opaque
 * shared materials, the ember its one self-lit material and the smoke its one translucent one, plus glow sprites.
 *
 * It flies: the hover is its rest pose (`WYVERN_HOVER`; the clips beat the wings and bob it, `die` drops it).
 * At rest the wings are spread flat, mid-beat. Bones: body; neck1 and neck2 up to the head (jaw hinged under
 * it); `sac`, the throat sac on neck2 (the clips scale it: it swells before a spit); `spit`, a tongue of fire at
 * the mouth on the head, built tiny so it is hidden until a clip scales it up (~×40 at full jet); tail1–4 down
 * the whip; each wing in three (arm at the shoulder, fore at the elbow, hand at the wrist); legs (thigh, shin).
 * The right wing is +X; on it +Z on `arm` lifts the wing, +Y sweeps it back, −Y on `fore` and +Y on `hand`
 * fold it (the left one mirrors). +X on body, neck and head pitches them nose down; +X opens the jaw, lifts
 * the tail and swings the legs back.
 */

/** Height of the model's origin over the ground while it flies (the clips drop it from here to die). */
export const WYVERN_HOVER = 1.15;
/** How small the tongue of fire is built (the clips scale `spit` by about 1 / SPIT_REST to show it). */
export const SPIT_REST = 0.025;

/** A torn membrane: a fan of quads from a hub to a ragged edge, with holes, whose edge vertices boil. */
class Membrane {
  readonly pos: number[] = [];
  readonly inner: number[] = [];
  readonly outer: number[] = [];
  readonly tips: number[] = [];
  readonly edge: THREE.Vector3[] = [];
  constructor(private readonly rng: Rng) {}
  private v(p: THREE.Vector3, tip = false): number {
    const i = this.pos.length / 3;
    this.pos.push(p.x, p.y, p.z);
    if (tip) this.tips.push(i);
    return i;
  }
  /**
   * The skin from `hub` to the edge a→b (both on bones), sagging toward the hub between them (`sag`), torn into
   * notches here and there, with `holes` burnt through (cells: ring 1..2, column).
   */
  panel(hub: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3, sag: number, holes: [number, number][] = [], S = 6, droop = 0.04): void {
    const RN = 3;
    const R = (): number => this.rng() - 0.5;
    const E: THREE.Vector3[] = [];
    for (let j = 0; j <= S; j++) {
      const u = j / S;
      const mid = j > 0 && j < S;
      // Scalloped: deepest midway; every other point torn deeper, and the torn ones droop.
      const tear = mid && (j % 2 === 1 || this.rng() < 0.25) ? 0.06 + this.rng() * 0.1 : 0;
      const p = a.clone().lerp(b, u + (mid ? R() * 0.05 : 0)).lerp(hub, Math.sin(u * Math.PI) * sag + tear);
      if (mid) p.add(V(R() * 0.03, -droop * Math.sin(u * Math.PI) + R() * 0.03, R() * 0.03));
      E.push(p);
      if (mid) this.edge.push(p.clone());
    }
    const h = this.v(hub);
    const id: number[][] = [];
    for (let i = 1; i <= RN; i++) {
      const row: number[] = [];
      for (let j = 0; j <= S; j++) {
        const k = i / RN;
        // The skin bellies a little between hub and edge.
        const p = hub.clone().lerp(E[j]!, k).add(V(0, -droop * Math.sin(k * Math.PI) * Math.sin((j / S) * Math.PI), 0));
        row.push(this.v(p, i === RN && j > 0 && j < S));
      }
      id.push(row);
    }
    const hole = new Set(holes.map(([r, c]) => `${r},${c}`));
    for (let j = 0; j < S; j++) this.inner.push(h, id[0]![j]!, id[0]![j + 1]!);
    for (let i = 1; i < RN; i++) {
      for (let j = 0; j < S; j++) {
        if (hole.has(`${i},${j}`)) continue;
        const a0 = id[i - 1]![j]!, a1 = id[i - 1]![j + 1]!, b0 = id[i]![j]!, b1 = id[i]![j + 1]!;
        (i === RN - 1 ? this.outer : this.inner).push(a0, b0, b1, a0, b1, a1);
      }
    }
  }
  /** The skin (`geo`) and its ashen, drybrushed outer band (`rim`) on the same vertices; rest positions to boil. */
  geometry(): { geo: THREE.BufferGeometry; rim: THREE.BufferGeometry; rest: Float32Array; tips: number[] } {
    const arr = new Float32Array(this.pos);
    const attr = new THREE.BufferAttribute(arr, 3);
    const make = (ix: number[]): THREE.BufferGeometry => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', attr);
      geo.setIndex(ix);
      geo.computeVertexNormals();
      return geo;
    };
    return { geo: make(this.inner), rim: make(this.outer), rest: arr.slice(), tips: this.tips };
  }
}

export const wyvern = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 61);
  const { g, rng, R } = c;
  g.position.y = WYVERN_HOVER;

  // Ash: soot-grey hide cracked like a cooled ember; charcoal for the belly, ridges and the inner skin of the
  // wings; a cold ash-pale horn for claws, teeth and barbs (no bone-beige); the Burn's heat in the seams.
  const hide = std('#3a383c', { roughness: 0.9, flatShading: true, bumpScale: 0.05 });
  const char = std('#18161a', { roughness: 0.75, flatShading: true, bumpScale: 0.05 });
  const horn = std('#8e8a8c', { roughness: 0.55, flatShading: true, bumpScale: 0.02 });
  const skin = std('#211e25', { roughness: 0.9, side: THREE.DoubleSide, bumpScale: 0.04 });
  const rimM = std('#423d47', { roughness: 0.95, side: THREE.DoubleSide, bumpScale: 0.05 });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.7);
  const wispM = basic('#2b2629', 0.42);

  /** A faceted lump of hide. */
  const lump = (parent: THREE.Object3D, mat: THREE.Material, r: number, p: THREE.Vector3, sx = 1, sy = 1, sz = 1, detail = 1): THREE.Mesh => {
    const m = M(new THREE.IcosahedronGeometry(r, detail), mat, parent);
    m.position.copy(p);
    m.scale.set(sx, sy, sz);
    m.rotation.set(R() * 0.5, R() * 0.5, R() * 0.5);
    return m;
  };
  /** A cone from `at` pointing along `dir` (a spike, a horn, a claw), flattened sideways by `flat`. */
  const spike = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, flat = 0.5, seg = 4): THREE.Mesh => {
    const d = dir.clone().normalize();
    const m = M(new THREE.ConeGeometry(w, len, seg), mat, parent);
    m.position.copy(at).addScaledVector(d, len * 0.5);
    m.quaternion.setFromUnitVectors(V(0, 1, 0), d);
    m.scale.z = flat;
    return m;
  };
  /** A jagged seam of ember over the hide: doubled, jittered points joined by a thin glowing tube. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.011): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p);
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.035, R() * 0.035, R() * 0.035)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), jit.length, r, 3), ember, parent);
  };

  /* ---------- bones ---------- */
  const bodyJ = joint(g, V(0, 0, 0.05), 'body');
  const NECK = [V(0, 0.07, 0.34), V(0, 0.17, 0.52), V(0, 0.29, 0.68), V(0, 0.35, 0.84)];
  const n1 = joint(bodyJ.abs, NECK[0]!, 'neck1');
  const n2 = joint(n1.abs, NECK[1]!.clone().lerp(NECK[2]!, 0.3), 'neck2');
  const headJ = joint(n2.abs, NECK[3]!, 'head');
  const jawJ = joint(headJ.abs, V(0, 0.315, 0.9), 'jaw');
  const sacJ = joint(n2.abs, V(0, 0.2, 0.76), 'sac');
  const spitJ = joint(headJ.abs, V(0, 0.29, 1.26), 'spit');
  const body = bodyJ.abs;

  /* ---------- trunk: lean, deep-chested, tapering into the tail ---------- */
  lump(body, hide, 0.2, V(0, 0.03, 0.17), 1.05, 0.95, 1.25);
  lump(body, hide, 0.16, V(0, 0.0, -0.13), 1.0, 0.92, 1.4);
  lump(body, hide, 0.14, V(0, -0.01, -0.4), 1.0, 0.9, 1.3);
  // Belly and keel in charcoal, the chest muscles that drive the wings bulging under the shoulders.
  lump(body, char, 0.14, V(0, -0.09, 0.06), 0.95, 0.7, 1.9);
  for (const k of [-1, 1]) lump(body, hide, 0.11, V(k * 0.12, 0.05, 0.22), 1.1, 0.9, 1.2, 0);
  // A ridge of charcoal spines down the back, tallest over the shoulders.
  for (let i = 0; i < 7; i++) {
    const u = i / 6;
    const z = 0.3 - u * 0.8;
    spike(body, char, V(0, 0.17 - u * 0.04 - Math.abs(u - 0.15) * 0.03, z), V(0, 1, -0.7), 0.15 - u * 0.06 + R() * 0.03, 0.035);
  }
  // The Burn's heat in the seams: across the chest, down the flanks and over the haunches.
  crack(body, [V(-0.13, 0.12, 0.33), V(-0.04, 0.06, 0.38), V(0.05, 0.08, 0.37), V(0.14, 0.02, 0.3)], 0.013);
  crack(body, [V(-0.02, -0.1, 0.32), V(0.01, -0.15, 0.18), V(-0.02, -0.16, 0.0), V(0.02, -0.14, -0.14)], 0.011);
  for (const k of [-1, 1]) {
    crack(body, [V(k * 0.19, 0.08, 0.16), V(k * 0.2, 0.0, 0.05), V(k * 0.16, -0.04, -0.1), V(k * 0.15, 0.03, -0.24), V(k * 0.13, -0.02, -0.4)], 0.011);
    crack(body, [V(k * 0.12, 0.11, -0.3), V(k * 0.14, 0.04, -0.45), V(k * 0.1, -0.07, -0.52)], 0.009);
  }
  glowSprite(body, '#ff4f3a', 0.3, V(0, -0.02, 0.4), 0.35);

  /* ---------- neck, head, jaw ---------- */
  const neck = new THREE.CatmullRomCurve3(NECK);
  // A sinuous neck: tapered sections overlapping at the vertebrae, a spine of small blades down the back.
  const NN = 5;
  for (let i = 0; i < NN; i++) {
    const t0 = (i / NN) * 0.94, t1 = ((i + 1) / NN) * 0.94;
    const bone = t0 < 0.3 ? n1.abs : n2.abs;
    const a = neck.getPoint(t0), b = neck.getPoint(t1);
    limb(bone, a, b, 0.095 - t0 * 0.04, 0.09 - t1 * 0.04, hide, 6);
    lump(bone, i % 2 ? char : hide, 0.075 - t0 * 0.03, a.clone().add(V(0, 0.02, 0)), 1.05, 0.9, 1.1, 0);
    spike(bone, char, a.clone().lerp(b, 0.5).add(V(0, 0.075 - t0 * 0.03, 0)), V(0, 1, -0.9), 0.1 - t0 * 0.03, 0.022);
  }
  // The throat seam: the fire inside shows down the underside of the neck.
  crack(n1.abs, [V(0, 0.0, 0.38), V(0.01, 0.07, 0.5), V(0, 0.12, 0.56)], 0.012);
  crack(n2.abs, [V(0, 0.12, 0.56), V(-0.01, 0.17, 0.66), V(0, 0.2, 0.72)], 0.012);

  // The throat sac: a pouch of fire under the jaw, caged in ribs of charcoal hide.
  const sac = sacJ.abs;
  const pouch = M(new THREE.SphereGeometry(0.07, 7, 5), ember, sac);
  pouch.position.set(0, 0.2, 0.76);
  pouch.scale.set(0.9, 0.85, 1.35);
  for (let i = 0; i < 3; i++) {
    const rib = M(new THREE.TorusGeometry(0.068, 0.016, 3, 7, Math.PI * 1.15), char, sac);
    rib.position.set(0, 0.205, 0.71 + i * 0.045);
    rib.rotation.set(0, 0, Math.PI * 1.43 + R() * 0.2);
    rib.scale.set(0.95 - Math.abs(i - 1) * 0.12, 0.9, 1);
  }
  const sacGlow = glowSprite(sac, '#ff5a2a', 0.42, V(0, 0.18, 0.78), 0.55, true);

  const head = headJ.abs;
  // A narrow skull drawn out into a long snout, low and wicked; the brow overhangs the eyes.
  lump(head, hide, 0.085, V(0, 0.37, 0.9), 0.95, 0.82, 1.3);
  const snout = M(new THREE.ConeGeometry(0.068, 0.36, 5), hide, head);
  snout.rotation.x = Math.PI / 2 + 0.1;
  snout.scale.set(0.9, 1, 0.62);
  snout.position.set(0, 0.35, 1.1);
  for (const k of [-1, 1]) {
    const brow = M(new THREE.BoxGeometry(0.05, 0.022, 0.1), char, head);
    brow.position.set(k * 0.045, 0.415, 0.95);
    brow.rotation.set(0.2, k * 0.3, -k * 0.35);
    // Two swept horns and a smaller pair under them, and a frill of spikes back along the jaw.
    const hp = [V(k * 0.045, 0.42, 0.88), V(k * 0.085, 0.47, 0.76), V(k * 0.11, 0.5, 0.63), V(k * 0.105, 0.56, 0.52)];
    for (let i = 0; i < 3; i++) limb(head, hp[i]!, hp[i + 1]!, 0.028 - i * 0.008, 0.02 - i * 0.008, i === 2 ? horn : char, 4);
    spike(head, horn, V(k * 0.07, 0.39, 0.84), V(k * 0.5, 0.1, -1), 0.13, 0.016, 1);
    spike(head, char, V(k * 0.06, 0.33, 0.86), V(k * 0.8, -0.2, -1), 0.09, 0.016, 1);
    // Ember eyes, set deep.
    M(new THREE.SphereGeometry(0.016, 5, 4), ember, head).position.set(k * 0.048, 0.395, 0.975);
    glowSprite(head, '#ff4f3a', 0.13, V(k * 0.055, 0.398, 0.985), 0.9);
    // Nostrils that smoulder.
    M(new THREE.SphereGeometry(0.009, 4, 3), ember, head).position.set(k * 0.02, 0.37, 1.25);
  }
  // Upper teeth, and the fire inside the mouth (seen when the jaw drops).
  for (let i = 0; i < 4; i++) {
    for (const k of [-1, 1]) spike(head, horn, V(k * (0.04 - i * 0.006), 0.32, 0.96 + i * 0.07), V(0, -1, 0.2), 0.04, 0.009, 1, 3);
  }
  const maw = M(new THREE.SphereGeometry(0.05, 6, 4), ember, head);
  maw.position.set(0, 0.32, 1.02);
  maw.scale.set(0.8, 0.35, 2.2);
  const jaw = jawJ.abs;
  const lower = M(new THREE.ConeGeometry(0.05, 0.33, 5), char, jaw);
  lower.rotation.x = Math.PI / 2 - 0.06;
  lower.scale.set(0.95, 1, 0.5);
  lower.position.set(0, 0.3, 1.07);
  for (let i = 0; i < 3; i++) for (const k of [-1, 1]) spike(jaw, horn, V(k * (0.032 - i * 0.006), 0.31, 0.99 + i * 0.07), V(0, 1, 0.2), 0.035, 0.008, 1, 3);
  // The tongue of fire it spits, built tiny at the mouth: a core and flames curling off it.
  const spit = spitJ.abs;
  const sp = spitJ.p.position;
  const flameAt = (len: number, w: number, off: THREE.Vector3, tilt: number): void => {
    const f = M(new THREE.ConeGeometry(w * SPIT_REST, len * SPIT_REST, 5), ember, spit);
    f.rotation.set(-Math.PI / 2 + tilt * 0.4, 0, 0);
    f.rotateOnWorldAxis(V(0, 1, 0), tilt * 0.6);
    f.position.set(sp.x + off.x * SPIT_REST, sp.y + off.y * SPIT_REST, sp.z + (off.z + len / 2) * SPIT_REST);
  };
  // A burst rather than a beam (the game draws the bolt): a core and tongues splaying out and curling.
  flameAt(0.5, 0.09, V(0, 0, 0), 0);
  flameAt(0.36, 0.07, V(0.04, 0.03, 0.02), 0.45);
  flameAt(0.36, 0.07, V(-0.04, 0.02, 0.03), -0.35);
  flameAt(0.3, 0.06, V(0.02, -0.04, 0.05), 0.15);
  flameAt(0.26, 0.05, V(-0.03, 0.05, 0.06), 0.8);

  /* ---------- wings: arm, forearm, a hand of three long fingers and a thumb claw ---------- */
  const skel: Record<string, THREE.Object3D> = { body: bodyJ.p, neck1: n1.p, neck2: n2.p, head: headJ.p, jaw: jawJ.p, sac: sacJ.p, spit: spitJ.p };
  const boil: { geo: THREE.BufferGeometry; rest: Float32Array; tips: number[]; amp: number }[] = [];
  const ribs: Ribbon[] = [];
  const sparks: { s: THREE.Sprite; p: THREE.Vector3; ph: number }[] = [];
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const X = (x: number, y: number, z: number): THREE.Vector3 => V(k * x, y, z);
    const sh = X(0.16, 0.11, 0.22), el = X(0.66, 0.3, 0.0), wr = X(1.1, 0.46, 0.18);
    const armJ = joint(body, sh, `arm${s}`);
    const foreJ = joint(armJ.abs, el, `fore${s}`);
    const handJ = joint(foreJ.abs, wr, `hand${s}`);
    skel[`arm${s}`] = armJ.p;
    skel[`fore${s}`] = foreJ.p;
    skel[`hand${s}`] = handJ.p;
    const arm = armJ.abs, fore = foreJ.abs, hand = handJ.abs;
    // The arm: a corded bone of hide, the elbow and wrist knuckled with charcoal.
    limb(arm, sh, el, 0.065, 0.045, hide, 6);
    lump(arm, hide, 0.08, X(0.3, 0.17, 0.15), 1.4, 0.9, 1.1, 0);
    lump(fore, char, 0.05, el, 1, 1, 1, 0);
    limb(fore, el, wr, 0.042, 0.032, hide, 5);
    lump(hand, char, 0.045, wr, 1.1, 1, 1, 0);
    crack(arm, [X(0.22, 0.16, 0.21), X(0.4, 0.23, 0.12), X(0.6, 0.3, 0.03)], 0.009);
    crack(fore, [X(0.73, 0.35, 0.04), X(0.92, 0.42, 0.12), X(1.06, 0.48, 0.17)], 0.008);
    // The thumb: a hooked claw on the leading edge.
    spike(hand, horn, X(1.11, 0.48, 0.2), X(0.25, 0.3, 1), 0.13, 0.022, 1);
    spike(hand, char, X(1.09, 0.43, 0.18), X(0.1, -0.4, 1), 0.08, 0.018, 1);
    // Three long fingers; a vein of ember runs inside each, under the skin.
    const tips = [X(1.82, 0.56, 0.0), X(1.66, 0.36, -0.52), X(1.26, 0.18, -0.84)];
    for (const [i, tp] of tips.entries()) {
      const knuckle = wr.clone().lerp(tp, 0.45).add(V(0, 0.03, 0.02));
      limb(hand, wr, knuckle, 0.026, 0.018, hide, 4);
      limb(hand, knuckle, tp, 0.018, 0.005, i === 0 ? horn : char, 4);
      M(new THREE.TubeGeometry(new THREE.LineCurve3(wr.clone().lerp(tp, 0.12), wr.clone().lerp(tp, 0.62)), 2, 0.008, 3), ember, hand);
    }
    // The membranes: between the fingers on the hand, elbow to the last finger on the forearm, the inner one
    // from the shoulder down to the flank on the arm. Holes burnt through, the trailing edge torn.
    const root = X(0.12, 0.0, -0.5);
    const mh = new Membrane(rng), mf = new Membrane(rng), ma = new Membrane(rng);
    mh.panel(wr, tips[0]!, tips[1]!, 0.18, k > 0 ? [[2, 3]] : [[1, 4]]);
    mh.panel(wr, tips[1]!, tips[2]!, 0.22, k > 0 ? [[1, 2], [2, 5]] : [[2, 2]]);
    mf.panel(el, tips[2]!, root, 0.2, k > 0 ? [[2, 1]] : [[2, 4], [1, 2]], 7, 0.07);
    ma.panel(sh, el, root, 0.04, [], 4, 0.03);
    for (const [mb, bone, amp] of [[mh, hand, 0.03], [mf, fore, 0.035], [ma, arm, 0.0]] as const) {
      // The mirrored side winds the other way; both faces are drawn, so only the normals care, and they are
      // recomputed from the same winding: flip the left so both wings shade alike from above.
      const gm = mb.geometry();
      if (k < 0) {
        for (const ix of [gm.geo, gm.rim]) {
          const a = ix.index!.array as Uint16Array | Uint32Array;
          for (let i = 0; i < a.length; i += 3) [a[i + 1], a[i + 2]] = [a[i + 2]!, a[i + 1]!];
          ix.computeVertexNormals();
        }
      }
      M(gm.geo, skin, bone);
      M(gm.rim, rimM, bone);
      if (amp > 0) boil.push({ geo: gm.geo, rest: gm.rest, tips: gm.tips, amp });
      // Smoke streams off the torn trailing edge, and sparks of ember smoulder on it.
      const edge = mb.edge;
      for (let i = 2; i < edge.length && amp > 0; i += 6) {
        const p = edge[i]!;
        ribs.push(new Ribbon(bone, [p.clone(), p.clone().add(V(k * 0.04, 0.05, -0.2)), p.clone().add(V(k * 0.02, 0.12, -0.42)), p.clone().add(V(k * 0.06, 0.2, -0.66))], 0.016 + rng() * 0.008, wispM, rng, 0.04));
      }
      for (let i = 0; i < edge.length; i += 4) sparks.push({ s: glowSprite(bone, '#ff5a2a', 0.09, edge[i]!, 0.7, true), p: edge[i]!.clone(), ph: rng() });
    }
  }

  /* ---------- tail: a long whip ending in a barb ---------- */
  const tailC = new THREE.CatmullRomCurve3([V(0, -0.0, -0.5), V(0.02, -0.06, -0.9), V(0.09, -0.12, -1.28), V(0.02, -0.15, -1.66), V(-0.07, -0.12, -2.0)]);
  const tailJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  for (let j = 0; j < 4; j++) {
    tailJ.push(joint(j ? tailJ[j - 1]!.abs : body, tailC.getPoint(j * 0.24), `tail${j + 1}`));
    skel[`tail${j + 1}`] = tailJ[j]!.p;
  }
  const tailBone = (t: number): THREE.Object3D => tailJ[Math.min(3, Math.floor(t / 0.24 + 1e-6))]!.abs;
  const TN = 10;
  for (let i = 0; i < TN; i++) {
    const t0 = i / TN, t1 = (i + 1) / TN;
    const bone = tailBone(t0 + 0.02);
    const a = tailC.getPoint(t0), b = tailC.getPoint(t1);
    limb(bone, a, b, 0.1 - t0 * 0.08, 0.1 - t1 * 0.08, i % 3 === 1 ? char : hide, 5);
    if (i % 2 === 0) lump(bone, hide, 0.085 - t0 * 0.065, a, 1.1, 0.9, 1.3, 0);
    if (i % 2 === 1 && t0 < 0.8) spike(bone, char, a.clone().add(V(0, 0.07 - t0 * 0.055, 0)), V(0, 1, -1), 0.09 - t0 * 0.05, 0.022);
  }
  // Seams along the whip, on alternate flanks.
  for (const [j, t0, k] of [[0, 0.03, 1], [1, 0.27, -1], [2, 0.5, 1]] as const) {
    const r = 0.085 - t0 * 0.07;
    crack(tailJ[j]!.abs, [0, 0.07, 0.14, 0.19].map((d, i) => tailC.getPoint(t0 + d).add(V(k * r * 0.9, r * (0.5 - (i % 2) * 0.6), 0))), 0.008);
  }
  // The barb: an arrowhead of pale horn with hooks thrown back, and a coal of heat in its root.
  const tip = tailJ[3]!.abs;
  const tp = tailC.getPoint(1);
  spike(tip, horn, tp.clone().add(V(0, 0, 0.04)), V(0, 0, -1), 0.24, 0.045, 0.35);
  for (const k of [-1, 1]) {
    spike(tip, horn, tp.clone().add(V(k * 0.02, 0, -0.02)), V(k * 1, 0, 0.55), 0.12, 0.022, 0.4);
    spike(tip, char, tp.clone().add(V(k * 0.015, 0, -0.1)), V(k * 0.9, 0.1, 0.7), 0.08, 0.016, 0.4);
  }
  M(new THREE.SphereGeometry(0.022, 5, 4), ember, tip).position.copy(tp).add(V(0, 0.012, 0.01));
  glowSprite(tip, '#ff4f3a', 0.16, tp.clone().add(V(0, 0.02, 0)), 0.6);
  ribs.push(new Ribbon(tip, [tp.clone().add(V(0, 0, -0.18)), tp.clone().add(V(0.04, 0.06, -0.38)), tp.clone().add(V(-0.03, 0.12, -0.6)), tp.clone().add(V(0.02, 0.2, -0.82))], 0.02, wispM, rng, 0.05));

  /* ---------- legs: drawn up under the belly, talons hooked ---------- */
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const hip = V(k * 0.12, -0.05, -0.3), knee = V(k * 0.17, -0.22, -0.12), ank = V(k * 0.15, -0.42, -0.3);
    const legJ = joint(body, hip, `leg${s}`);
    const shinJ = joint(legJ.abs, knee, `shin${s}`);
    skel[`leg${s}`] = legJ.p;
    skel[`shin${s}`] = shinJ.p;
    lump(legJ.abs, hide, 0.09, hip.clone().lerp(knee, 0.4), 1, 1.2, 1.15, 0);
    limb(legJ.abs, hip, knee, 0.07, 0.045, hide, 5);
    limb(shinJ.abs, knee, ank, 0.042, 0.03, char, 5);
    lump(shinJ.abs, char, 0.035, ank, 1, 1, 1, 0);
    crack(legJ.abs, [V(k * 0.17, -0.05, -0.22), V(k * 0.2, -0.13, -0.17), V(k * 0.19, -0.2, -0.13)], 0.008);
    for (let i = 0; i < 3; i++) {
      const a = (i - 1) * 0.45;
      spike(shinJ.abs, horn, ank.clone().add(V(Math.sin(a) * 0.02 * k, -0.01, 0.01)), V(Math.sin(a) * 0.6 * k, -1, 0.75), 0.1, 0.014, 1, 4);
    }
    spike(shinJ.abs, horn, ank.clone().add(V(0, 0, -0.02)), V(0, -0.6, -1), 0.07, 0.012, 1, 4);
  }

  // Embers shed off its back, drifting up and back and dying out.
  const embers: { s: THREE.Sprite; x: number; z: number; ph: number }[] = [];
  for (let i = 0; i < 4; i++) embers.push({ s: glowSprite(body, '#ff6a2a', 0.08, V(0, 0, 0), 0.8, true), x: R() * 0.3, z: 0.2 - rng() * 0.6, ph: i / 4 });

  const t = (n: number): void => {
    for (const r of ribs) r.step();
    for (const b of boil) {
      const p = b.geo.attributes.position as THREE.BufferAttribute;
      const a = p.array as Float32Array;
      a.set(b.rest);
      for (const i of b.tips) {
        a[i * 3] = b.rest[i * 3]! + R() * b.amp;
        a[i * 3 + 1] = b.rest[i * 3 + 1]! + R() * b.amp;
        a[i * 3 + 2] = b.rest[i * 3 + 2]! + R() * b.amp * 1.4;
      }
      p.needsUpdate = true;
    }
    for (const s of sparks) {
      const q = (s.ph + n * 0.31) % 1;
      s.s.position.set(s.p.x, s.p.y + q * 0.08, s.p.z - q * 0.2);
      s.s.material.opacity = 0.8 * (1 - q) * (q < 0.1 ? q / 0.1 : 1);
    }
    for (const e of embers) {
      const q = (e.ph + n * 0.23) % 1;
      e.s.position.set(e.x + Math.sin(q * 5 + e.ph * 9) * 0.05, 0.15 + q * 0.2, e.z - q * 0.5);
      e.s.material.opacity = 0.85 * (1 - q) * (q < 0.08 ? q / 0.08 : 1);
    }
    sacGlow.material.opacity = 0.45 + 0.15 * Math.sin(n * 2.1) + R() * 0.08;
  };
  t(0);
  return done(c, WYVERN_HOVER + 0.55, t, undefined, bonesOf(c, skel));
};
